const failure =
  "Express checkout could not be completed. Please try again or continue through your shopping bag.";

export function validateExpressQuote(quote) {
  if (
    !Number.isSafeInteger(quote?.amount) ||
    quote.amount < 50 ||
    !Array.isArray(quote.lineItems) ||
    !Array.isArray(quote.shippingRates) ||
    quote.lineItems.some(
      (item) => !Number.isSafeInteger(item.amount) || item.amount < 0
    ) ||
    quote.lineItems.reduce((sum, item) => sum + item.amount, 0) !==
      quote.amount ||
    !quote.shippingRates.length ||
    quote.shippingRates.some(
      (rate) =>
        !rate.id || !Number.isSafeInteger(rate.amount) || rate.amount < 0
    )
  ) {
    throw new Error(failure);
  }
  return quote;
}

// All wallet callbacks share one sequenced flow; no component/store side effects
// occur while quoting. The server alone owns prices, addresses and order binding.
export function createExpressFlow({
  initial,
  request,
  elements,
  stripe,
  onBusy,
  onError,
  onBegin = () => {},
  onPrepared = () => {},
  onFailed = () => {},
  navigate,
  comment = () => "",
}) {
  let quote = validateExpressQuote(initial);
  let address = null;
  let generation = 0;
  let active = false;
  let confirming = false;
  let disposed = false;
  let queue = Promise.resolve();
  const update = (value) => {
    quote = validateExpressQuote(value);
    elements.update({ amount: quote.amount });
  };
  const fail = (error) => onError(error?.message || failure);
  const enqueue = (event, input) => {
    const current = generation;
    queue = queue.then(async () => {
      if (disposed || current !== generation || !active) {
        event.reject();
        return;
      }
      try {
        const result = await request({ action: "quote", ...input });
        if (disposed || current !== generation || !active) {
          event.reject();
          return;
        }
        if (result.status !== "ready" || !result.quoteId)
          throw new Error(result.message || failure);
        update(result);
        event.resolve({
          lineItems: quote.lineItems,
          shippingRates: quote.shippingRates,
        });
      } catch (error) {
        quote = { ...quote, quoteId: undefined };
        event.reject();
        fail(error);
      }
    });
    return queue;
  };
  return {
    click(event) {
      if (disposed || confirming) return;
      generation += 1;
      active = true;
      address = null;
      // Quotes from a previous sheet cannot authorize this attempt.
      quote = { ...quote, quoteId: undefined };
      onBusy(true);
      onError("");
      onBegin(event.expressPaymentType);
      // Stripe requires resolve within one second; never wait on a network here.
      event.resolve({
        emailRequired: true,
        billingAddressRequired: true,
        shippingAddressRequired: true,
        allowedShippingCountries: initial.countries,
        lineItems: quote.lineItems,
        shippingRates: quote.shippingRates,
      });
    },
    shippingAddressChange(event) {
      address = event.address;
      return enqueue(event, { address, shippingRate: "" });
    },
    shippingRateChange(event) {
      return enqueue(event, { address, shippingRate: event.shippingRate.id });
    },
    async confirm(event) {
      if (disposed || confirming || !active) return;
      confirming = true;
      onBusy(true);
      onError("");
      try {
        await queue;
        if (disposed || !active)
          throw new Error("Express checkout was closed.");
        if (
          !quote.quoteId ||
          !event.shippingAddress ||
          !event.billingDetails ||
          event.shippingRate?.id !== quote.shippingRate
        )
          throw new Error(
            "Please select your delivery address and shipping option again."
          );
        const submitted = await elements.submit();
        if (submitted.error) throw submitted.error;
        if (disposed || !active)
          throw new Error("Express checkout was closed.");
        const result = await request({
          action: "prepare",
          quoteId: quote.quoteId,
          amount: quote.amount,
          shippingRate: quote.shippingRate,
          shippingAddress: event.shippingAddress,
          billingDetails: event.billingDetails,
          termsAccepted: true,
          comment: comment(),
        });
        if (disposed || !active)
          throw new Error("Express checkout was closed.");
        if (result.status === "changed") {
          update(result);
          throw new Error(result.message);
        }
        if (
          result.status !== "prepared" ||
          !result.clientSecret ||
          !result.orderId ||
          !result.returnUrl
        ) {
          throw new Error(result.message || failure);
        }
        await onPrepared(result, event);
        if (disposed || !active)
          throw new Error("Express checkout was closed.");
        const payment = await stripe.confirmPayment({
          elements,
          clientSecret: result.clientSecret,
          confirmParams: { return_url: result.returnUrl },
          redirect: "if_required",
        });
        if (payment.error) throw payment.error;
        const intent = payment.paymentIntent;
        if (
          !intent ||
          intent.id !== result.paymentIntentId ||
          !["succeeded", "processing"].includes(intent.status)
        ) {
          throw new Error(
            "Your payment needs another step. Please return to checkout to check its status."
          );
        }
        // The callback verifies payment server-side before redirecting to success.
        // Never navigate directly to the success page or emit purchase here.
        const url = new URL(result.returnUrl);
        url.searchParams.set("payment_intent", intent.id);
        active = false;
        navigate(url.toString());
      } catch (error) {
        event.paymentFailed({ reason: "fail" });
        onFailed();
        fail(error);
      } finally {
        confirming = false;
        // Link keeps its sheet open after paymentFailed. A deliberate retry
        // must revalidate prepare and reuse the server's order/PI binding.
        // Only success, cancel or disposal ends the current wallet attempt.
        onBusy(false);
      }
    },
    cancel() {
      if (confirming) return;
      generation += 1;
      active = false;
      address = null;
      quote = { ...initial };
      elements.update({ amount: initial.amount });
      onBusy(false);
    },
    dispose() {
      disposed = true;
      generation += 1;
      active = false;
    },
  };
}
